import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Modal } from '@/components/modal';
import { useGateChecks } from '@/hooks/queries/use-gate-checks';
import { useCreateWave, useUpdateWave } from '@/hooks/mutations/use-wave-admin';
import { QualificationWave } from '@/services/workflow/qualification';

// `datetime-local` wants no zone; the API wants ISO. Convert at the edges
// rather than storing two shapes.
const toLocalInput = (iso: string) => {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const toIso = (local: string) => new Date(local).toISOString();

const plusDays = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setMinutes(0, 0, 0);
  return toLocalInput(d.toISOString());
};

interface WaveEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: string;
  region: string;
  /** Null creates; a wave amends it. Client and region are fixed at creation. */
  wave: QualificationWave | null;
}

export const WaveEditorModal: FC<WaveEditorModalProps> = ({
  isOpen,
  onClose,
  client,
  region,
  wave,
}) => {
  const { data: gateChecks, isLoading: checksLoading } = useGateChecks();
  const create = useCreateWave(onClose);
  const update = useUpdateWave(onClose);

  const [name, setName] = useState('');
  const [preOpens, setPreOpens] = useState('');
  const [preCloses, setPreCloses] = useState('');
  const [uatOpens, setUatOpens] = useState('');
  const [uatCloses, setUatCloses] = useState('');
  const [required, setRequired] = useState<string[]>([]);
  const [advisory, setAdvisory] = useState<string[]>([]);
  const [clientSignOff, setClientSignOff] = useState(true);

  useEffect(() => {
    if (!isOpen) return;
    if (wave) {
      setName(wave.name);
      setPreOpens(toLocalInput(wave.pre_uat_opens_at));
      setPreCloses(toLocalInput(wave.pre_uat_closes_at));
      setUatOpens(toLocalInput(wave.uat_opens_at));
      setUatCloses(toLocalInput(wave.uat_closes_at));
      setRequired(wave.required_checks);
      setAdvisory(wave.advisory_checks);
      setClientSignOff(wave.requires_client_sign_off);
      return;
    }
    setName('');
    setPreOpens(plusDays(0));
    setPreCloses(plusDays(7));
    setUatOpens(plusDays(7));
    setUatCloses(plusDays(21));
    setRequired(['end_to_end_run']);
    setAdvisory([]);
    setClientSignOff(true);
  }, [isOpen, wave]);

  const toggle = (
    key: string,
    list: string[],
    setList: (next: string[]) => void,
    other: string[],
    setOther: (next: string[]) => void,
  ) => {
    if (list.includes(key)) {
      setList(list.filter((k) => k !== key));
      return;
    }
    // A check is required or advisory, never both: the gate would run it
    // twice and report two results for one condition.
    setOther(other.filter((k) => k !== key));
    setList([...list, key]);
  };

  // The table CHECK requires pre_uat_closes_at <= uat_opens_at, and a gap
  // between them is a wave nobody can traverse. Catching it here turns a
  // 422 into a sentence.
  const windowProblem = useMemo(() => {
    if (!preOpens || !preCloses || !uatOpens || !uatCloses) {
      return 'Every window needs both ends.';
    }
    if (new Date(preCloses) <= new Date(preOpens)) {
      return 'Pre-UAT has to close after it opens.';
    }
    if (new Date(uatCloses) <= new Date(uatOpens)) {
      return 'UAT has to close after it opens.';
    }
    // The two windows MAY overlap: pre-UAT gates applying, UAT gates
    // promoting, and a real wave runs those in parallel.
    if (new Date(uatOpens) < new Date(preOpens)) {
      return 'UAT cannot open before the wave itself does.';
    }
    if (new Date(uatCloses) < new Date(preCloses)) {
      return 'UAT cannot close before entries stop, or anything admitted late could never be promoted.';
    }
    return null;
  }, [preOpens, preCloses, uatOpens, uatCloses]);

  const signOffWarning =
    !!wave && wave.requires_client_sign_off && !clientSignOff;

  const isSubmitting = create.isPending || update.isPending;
  const canSave = !!name.trim() && !windowProblem && !isSubmitting;

  const submit = () => {
    const windows = {
      pre_uat_opens_at: toIso(preOpens),
      pre_uat_closes_at: toIso(preCloses),
      uat_opens_at: toIso(uatOpens),
      uat_closes_at: toIso(uatCloses),
    };
    if (wave) {
      update.mutate({
        waveId: wave.id,
        patch: {
          name: name.trim(),
          ...windows,
          required_checks: required,
          advisory_checks: advisory,
          requires_client_sign_off: clientSignOff,
        },
      });
      return;
    }
    create.mutate({
      client,
      region,
      name: name.trim(),
      ...windows,
      required_checks: required,
      advisory_checks: advisory,
      requires_client_sign_off: clientSignOff,
    });
  };

  const checkList = (
    list: string[],
    setList: (next: string[]) => void,
    other: string[],
    setOther: (next: string[]) => void,
    idPrefix: string,
  ) => (
    <div className='space-y-1.5'>
      {(gateChecks ?? []).map((check) => (
        <label
          key={check.key}
          htmlFor={`${idPrefix}-${check.key}`}
          className='flex items-start gap-2 text-sm cursor-pointer'
        >
          <Checkbox
            id={`${idPrefix}-${check.key}`}
            checked={list.includes(check.key)}
            onCheckedChange={() =>
              toggle(check.key, list, setList, other, setOther)
            }
          />
          <span>
            <span className='font-mono text-xs'>{check.key}</span>
            <span className='block text-xs text-muted-foreground'>
              {check.description}
            </span>
          </span>
        </label>
      ))}
    </div>
  );

  let body: ReactNode = null;
  if (checksLoading) {
    body = (
      <div className='flex items-center gap-2 py-6 text-sm text-muted-foreground'>
        <Loader2 className='size-4 animate-spin' />
        Loading the check list...
      </div>
    );
  } else {
    body = (
      <div className='space-y-4 py-2'>
        <div className='space-y-1.5'>
          <Label htmlFor='wave-name'>Name</Label>
          <Input
            id='wave-name'
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='Wave 2'
          />
          <p className='text-xs text-muted-foreground'>
            {client} · {region}. Both are fixed once the wave exists.
          </p>
        </div>

        <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
          <div className='space-y-1.5'>
            <Label htmlFor='pre-opens'>Pre-UAT opens</Label>
            <Input
              id='pre-opens'
              type='datetime-local'
              value={preOpens}
              onChange={(e) => setPreOpens(e.target.value)}
            />
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='pre-closes'>Pre-UAT closes</Label>
            <Input
              id='pre-closes'
              type='datetime-local'
              value={preCloses}
              onChange={(e) => setPreCloses(e.target.value)}
            />
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='uat-opens'>UAT opens</Label>
            <Input
              id='uat-opens'
              type='datetime-local'
              value={uatOpens}
              onChange={(e) => setUatOpens(e.target.value)}
            />
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='uat-closes'>UAT closes</Label>
            <Input
              id='uat-closes'
              type='datetime-local'
              value={uatCloses}
              onChange={(e) => setUatCloses(e.target.value)}
            />
          </div>
        </div>

        {windowProblem && (
          <p className='flex items-start gap-2 text-sm text-red-700 dark:text-red-400'>
            <AlertCircle className='size-4 shrink-0 mt-0.5' />
            {windowProblem}
          </p>
        )}

        <div className='space-y-2'>
          <Label>Entry conditions, blocking</Label>
          {checkList(required, setRequired, advisory, setAdvisory, 'req')}
        </div>

        <div className='space-y-2'>
          <Label>Advisory, recorded but not blocking</Label>
          {checkList(advisory, setAdvisory, required, setRequired, 'adv')}
        </div>

        <div className='flex items-start justify-between gap-4 rounded-md bg-muted p-3'>
          <div>
            <Label htmlFor='client-sign-off'>Client sign-off required</Label>
            <p className='text-xs text-muted-foreground'>
              Off means Capitol alone can settle the pre-UAT gate.
            </p>
          </div>
          <Switch
            id='client-sign-off'
            checked={clientSignOff}
            onCheckedChange={setClientSignOff}
          />
        </div>

        {signOffWarning && (
          <p className='flex items-start gap-2 text-sm text-muted-foreground'>
            <AlertCircle className='size-4 shrink-0 mt-0.5' />
            This applies to workflows applied from now on. Anything already in
            pre-UAT keeps the sides it was admitted under.
          </p>
        )}
      </div>
    );
  }

  return (
    <Modal
      title={wave ? 'Amend wave' : 'New wave'}
      isDialogOpen={isOpen}
      setIsDialogOpen={(open) => {
        if (!open) onClose();
      }}
      contentClassName='sm:max-w-xl'
      footer={
        <>
          <Button variant='outline' onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSave} className='gap-2'>
            {isSubmitting && <Loader2 className='size-4 animate-spin' />}
            {wave ? 'Save changes' : 'Create wave'}
          </Button>
        </>
      }
    >
      {body}
    </Modal>
  );
};
